import React, { useState, useEffect, useRef } from 'react';
import {
  FiBell,
  FiCalendar,
  FiShield,
  FiFileText,
  FiCheckCircle,
  FiAlertTriangle,
  FiExternalLink,
} from 'react-icons/fi';
import type { AppNotification } from '../services/notificationService';
import { getLocale, t as translateGlobal, useI18n } from '../i18n';
import './NotificationDropdown.css';

interface Props {
  notifications: AppNotification[];
  unreadCount: number;
  onMarkAllRead: () => void;
  onMarkRead: (ids: string[]) => void;
  onGoTo: (notification: AppNotification) => void;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return translateGlobal('just now');
  if (mins < 60) return translateGlobal('{count}m ago', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return translateGlobal('{count}h ago', { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return translateGlobal('{count}d ago', { count: days });
  return new Intl.DateTimeFormat(getLocale(), { day: 'numeric', month: 'short' }).format(
    new Date(dateStr),
  );
}

const typeIcon: Record<AppNotification['type'], React.ReactNode> = {
  shift_assigned: <FiCalendar />,
  shift_updated: <FiCalendar />,
  licence_submitted: <FiShield />,
  licence_reviewed: <FiShield />,
  report_assigned: <FiFileText />,
  report_updated: <FiFileText />,
  report_comment: <FiFileText />,
  task_updated: <FiCheckCircle />,
  alarm_triggered: <FiAlertTriangle />,
};

export default function NotificationDropdown({
  notifications,
  unreadCount,
  onMarkAllRead,
  onMarkRead,
  onGoTo,
}: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleGoTo = (event: React.MouseEvent<HTMLButtonElement>, notification: AppNotification) => {
    event.stopPropagation();
    if (!notification.read_at) {
      onMarkRead([notification.id]);
    }
    onGoTo(notification);
    setOpen(false);
  };

  return (
    <div className="notif-dropdown" ref={ref}>
      <button
        type="button"
        className={`notif-bell ${unreadCount > 0 ? 'notif-bell--active' : ''}`}
        onClick={() => (open ? setOpen(false) : handleOpen())}
        aria-label={`${t('Notifications')}${unreadCount > 0 ? `, ${t('{count} unread', { count: unreadCount })}` : ''}`}
      >
        <FiBell />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-panel__header">
            <span className="notif-panel__title">{t('Notifications')}</span>
            {notifications.some((n) => !n.read_at) && (
              <button type="button" className="notif-mark-all" onClick={onMarkAllRead}>
                {t('Mark all read')}
              </button>
            )}
          </div>

          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">{t('No notifications yet')}</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`notif-item ${!n.read_at ? 'notif-item--unread' : ''}`}
                  onClick={() => !n.read_at && onMarkRead([n.id])}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && !n.read_at && onMarkRead([n.id])}
                >
                  <span className={`notif-item__icon notif-item__icon--${n.type}`}>
                    {typeIcon[n.type]}
                  </span>
                  <div className="notif-item__content">
                    <div className="notif-item__title">{n.title}</div>
                    <div className="notif-item__body">{n.body}</div>
                    <div className="notif-item__footer">
                      <span className="notif-item__time">{timeAgo(n.created_at)}</span>
                      <button
                        type="button"
                        className="notif-item__goto"
                        onClick={(event) => handleGoTo(event, n)}
                      >
                        <FiExternalLink aria-hidden="true" />
                        <span>{t('Go To')}</span>
                      </button>
                    </div>
                  </div>
                  {!n.read_at && <span className="notif-item__dot" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
